import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { Product, TimePrice } from '../types';

interface CartItem {
  product: Product;
  quantity: number;
  selectedTimePrice?: TimePrice;
}

interface CartStore {
  items: CartItem[];
  addItem: (product: Product, selectedTimePrice?: TimePrice) => void;
  removeItem: (productId: string) => void;
  updateQuantity: (productId: string, quantity: number) => void;
  clearCart: () => void;
  getTotalItems: () => number;
  getTotalPrice: () => number;
}

const calculateProductPrice = (product: Product, selectedTimePrice?: TimePrice) => {
  const basePrice = selectedTimePrice?.price || product.price;
  if (product.discountPercentage) {
    return basePrice * (1 - product.discountPercentage / 100);
  }
  return basePrice;
};

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      addItem: (product, selectedTimePrice) => {
        set((state) => {
          // Dispatch custom event for notifications
          window.dispatchEvent(new CustomEvent('cart-item-added', { 
            detail: { 
              product,
              user: 'Tú' // Identifier for the current user
            } 
          }));

          const existingItem = state.items.find(
            (item) => item.product.id === product.id && 
                     (!selectedTimePrice || item.selectedTimePrice?.id === selectedTimePrice.id)
          );

          if (existingItem) {
            return {
              items: state.items.map((item) =>
                item.product.id === product.id && 
                (!selectedTimePrice || item.selectedTimePrice?.id === selectedTimePrice.id)
                  ? { ...item, quantity: item.quantity + 1 }
                  : item
              ),
            };
          }

          return {
            items: [...state.items, { 
              product, 
              quantity: 1,
              selectedTimePrice 
            }],
          };
        });
      },
      removeItem: (productId) => {
        set((state) => ({
          items: state.items.filter((item) => item.product.id !== productId),
        }));
      },
      updateQuantity: (productId, quantity) => {
        set((state) => ({
          items: state.items.map((item) =>
            item.product.id === productId
              ? { ...item, quantity: Math.max(0, quantity) }
              : item
          ),
        }));
      },
      clearCart: () => {
        set({ items: [] });
      },
      getTotalItems: () => {
        const state = get();
        return state.items.reduce((total, item) => total + item.quantity, 0);
      },
      getTotalPrice: () => {
        const state = get();
        return state.items.reduce(
          (total, item) => {
            const price = calculateProductPrice(item.product, item.selectedTimePrice);
            return total + (price * item.quantity);
          },
          0
        );
      },
    }),
    {
      name: 'cart-storage',
    }
  )
);